import * as Yup from "yup";
import { useFormik } from "formik";
import { useCallback, useMemo } from "react";

import { useAddMenuItem } from "./useAddMenuItem";
import {
  AddItemInput,
  MenuItem,
  MenuItemOption,
  MenuItemOptionElement,
  MenuItemTag,
} from "src/interfaces/menu.interface";
import { MutationCallbacks } from "src/interfaces/menu-items-props.interface";
import { buildLocaleTranslationMap, buildTranslationsPayload, syncLegacyFieldsFromLocaleTranslations } from "src/utils/localeTranslations";

const addItemSchema = Yup.object({
  abbreviation: Yup.string().nullable(),

  categoryCorrelationId: Yup.string().uuid("Invalid categoryCorrelationId format"),

  englishDescription: Yup.string().nullable(),

  englishName: Yup.string().required("Item name is required"),

  itemOptionInputList: Yup.array()
    .of(
      Yup.object({
        addItemOptionElementInputList: Yup.array()
          .of(
            Yup.object({
              description: Yup.string().nullable(),
              itemOptionCorrelationId: Yup.string()
                .uuid("Invalid itemOptionCorrelationId format")
                .nullable(),
              nonEnglishName: Yup.string().nullable(),
              name: Yup.string()
                .trim()
                .test("name-required-when-touched", "Name is required", function (value) {
                  const menuItemOptionElement = this.parent;
                  const hasMenuItemValue =
                    !!menuItemOptionElement.name ||
                    menuItemOptionElement.renderSequence != null ||
                    menuItemOptionElement.priceCents != null;

                  return (
                    !hasMenuItemValue ||
                    (hasMenuItemValue && !!value && value.trim().length > 0)
                  );
                }),
              priceCents: Yup.number()
                .typeError("Price must be a number")
                .integer("Price must be an integer")
                .min(0, "Price must be positive")
                .test("price-required-when-touched", "Price is required", function (value) {
                  const menuItemOptionElement = this.parent;
                  const hasMenuItemValue =
                    !!menuItemOptionElement.name ||
                    menuItemOptionElement.renderSequence != null ||
                    menuItemOptionElement.priceCents != null;

                  return (
                    !hasMenuItemValue ||
                    (hasMenuItemValue && value !== undefined && value !== null && value >= 0)
                  );
                }),
              renderSequence: Yup.number()
                .typeError("Render sequence must be a number")
                .integer("Render sequence must be an integer")
                .test("sequence-required-when-touched", "Render sequence is required", function (value) {
                  const menuItemOptionElement = this.parent;
                  const hasMenuItemValue =
                    !!menuItemOptionElement.name || menuItemOptionElement.renderSequence != null || menuItemOptionElement.priceCents != null;

                  return (
                    !hasMenuItemValue ||
                    (hasMenuItemValue && value !== undefined && value !== null && value >= 0)
                  );
                }),
            })
          )
          .nullable(),
        description: Yup.string().nullable(),
        isRequired: Yup.boolean().typeError("isRequired must be true or false"),
        itemCorrelationId: Yup.string().uuid("Invalid itemCorrelationId format").nullable(),
        multipleSelectionsAllowed: Yup.boolean().typeError("multipleSelectionsAllowed must be true or false"),
        nonEnglishName: Yup.string().nullable(),
        name: Yup.string()
          .trim()
          .test("option-name-required", "Option name is required", function (value) {
            const menuItemOption = this.parent;
            const hasMenuItemValue =
              !!menuItemOption.name?.trim() ||
              menuItemOption.renderSequence != null ||
              (Array.isArray(menuItemOption.addItemOptionElementInputList) &&
                menuItemOption.addItemOptionElementInputList.length > 0);

            return !hasMenuItemValue || (hasMenuItemValue && !!value && value.trim().length > 0);
          }),
        renderSequence: Yup.number()
          .typeError("Render sequence must be a number")
          .integer("Render sequence must be an integer")
          .test("option-sequence-required", "Render sequence is required", function (value) {
            const menuItemOption = this.parent;
            const hasMenuItemValue =
              !!menuItemOption.name?.trim() ||
              menuItemOption.renderSequence != null ||
              (Array.isArray(menuItemOption.addItemOptionElementInputList) && menuItemOption.addItemOptionElementInputList.length > 0);

            return !hasMenuItemValue || (hasMenuItemValue && value !== undefined && value !== null && value > 0);
          }),
      })
    )
    .nullable(),

  itemTagCorrelationIdList: Yup.array().of(Yup.string().uuid("Invalid tag ID format")).nullable(),

  nonEnglishDescription: Yup.string().nullable(),
  nonEnglishName: Yup.string().nullable(),

  priceCents: Yup.number()
    .typeError("Price must be a number")
    .integer("Price must be an integer")
    .min(0, "Price must be positive")
    .required("Price is required"),
  storePrinterCorrelationId: Yup.string()
    .nullable()
    .transform((value) => {
      if (value === "" || value === undefined) {
        return null;
      }
      return value;
    })
    .test("uuid-format", "Invalid storePrinterCorrelationId format", function (value) {
      if (value === null || value === "" || value === undefined) {
        return true;
      }
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
    }),
  taxRate: Yup.number().typeError("Tax rate must be a number").required("Tax rate is required"),

  isActive: Yup.boolean().optional(),
  isQuickServiceChoiceItem: Yup.boolean().optional(),
  enterpriseEnabled: Yup.boolean().optional(),
});

/** Single source of truth for item → form shape (used by Formik and dirty baseline). */
export const mapMenuItemToFormValues = (
  menuItemToEdit: MenuItem,
  enabledLocales: string[] = ["en-US"]
) => {
  if (!menuItemToEdit) return null;

  const localeTranslations = buildLocaleTranslationMap(
    menuItemToEdit.translations,
    enabledLocales,
    {
      englishName: menuItemToEdit.englishName,
      nonEnglishName: menuItemToEdit.nonEnglishName,
      englishDescription: menuItemToEdit.englishDescription,
      nonEnglishDescription: menuItemToEdit.nonEnglishDescription,
    }
  );

  return {
    abbreviation: menuItemToEdit.abbreviation || "",
    categoryCorrelationId: (menuItemToEdit as any).cetogoryCorrelationId || "", // keep legacy field name
    englishDescription: menuItemToEdit.englishDescription || "",
    englishName: menuItemToEdit.englishName || "",
    isQuickServiceChoiceItem: menuItemToEdit.isQuickServiceChoiceItem === true,
    enterpriseEnabled: menuItemToEdit.enterpriseEnabled === true,
    itemOptionInputList:
      menuItemToEdit.itemOptions?.map((menuItemOption: MenuItemOption) => ({
        correlationId: menuItemOption.correlationId || "",
        addItemOptionElementInputList:
          menuItemOption.itemOptionElements?.map((menuItemOptionElement: MenuItemOptionElement) => {
            const elementLocaleTranslations = buildLocaleTranslationMap(
              menuItemOptionElement.translations,
              enabledLocales,
              {
                englishName: menuItemOptionElement.name,
                nonEnglishName: menuItemOptionElement.nonEnglishName,
              }
            );
            return {
              correlationId: menuItemOptionElement.correlationId || "",
              description: menuItemOptionElement.description || "",
              name: menuItemOptionElement.name || "",
              nonEnglishName: menuItemOptionElement.nonEnglishName || "",
              priceCents: menuItemOptionElement.priceCents ?? 0,
              renderSequence: menuItemOptionElement.renderSequence ?? 1,
              translations: menuItemOptionElement.translations,
              localeTranslations: elementLocaleTranslations,
            };
          }) || [],
        description: menuItemOption.description || "",
        isRequired: menuItemOption.isRequired ?? false,
        itemCorrelationId: menuItemToEdit.correlationId || "",
        multipleSelectionsAllowed: menuItemOption.multipleSelectionsAllowed ?? false,
        name: menuItemOption.name || "",
        nonEnglishName: menuItemOption.nonEnglishName || "",
        renderSequence: menuItemOption.renderSequence ?? 1,
        translations: menuItemOption.translations,
        localeTranslations: buildLocaleTranslationMap(menuItemOption.translations, enabledLocales, {
          englishName: menuItemOption.name,
          nonEnglishName: menuItemOption.nonEnglishName,
        }),
      })) || [],
    itemTagCorrelationIdList: menuItemToEdit.itemTags?.map((menuItemTag: MenuItemTag) => menuItemTag.tagCorrelationId) || [],
    nonEnglishDescription: menuItemToEdit.nonEnglishDescription || "",
    nonEnglishName: menuItemToEdit.nonEnglishName || "",
    isActive: menuItemToEdit.active !== false,
    priceCents: menuItemToEdit.priceCents ?? 0,
    storePrinterCorrelationId: menuItemToEdit.printer?.correlationId || "",
    taxRate: menuItemToEdit.taxRate ?? 0,
    localeTranslations,
  };
};

export const useMenuItemsForm = (
  menuCategoryId: string,
  menuItemToEdit: MenuItem | null,
  enabledLocales: string[] = ["en-US"]
) => {
  const { addMenuItem: addMenuItemMutation, isAddingMenuItem } = useAddMenuItem();

  const initialValues = useMemo(() => {
    if (menuItemToEdit) {
      return mapMenuItemToFormValues(menuItemToEdit, enabledLocales) as AddItemInput;
    }

    const emptyLocaleTranslations = buildLocaleTranslationMap(undefined, enabledLocales, {});
    return {
      abbreviation: "",
      categoryCorrelationId: menuCategoryId,
      englishDescription: "",
      englishName: "",
      isActive: true,
      isQuickServiceChoiceItem: false,
      enterpriseEnabled: false,
      itemOptionInputList: [],
      itemTagCorrelationIdList: [],
      nonEnglishDescription: "",
      nonEnglishName: "",
      priceCents: 0,
      storePrinterCorrelationId: "",
      taxRate: 0,
      localeTranslations: emptyLocaleTranslations,
    };
  }, [menuCategoryId, menuItemToEdit, menuItemToEdit?.printer?.correlationId, enabledLocales]);

  const formik = useFormik<AddItemInput>({
    enableReinitialize: true,
    initialValues,
    validationSchema: addItemSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  });

  const { values } = formik;

  const addMenuItem = useCallback(
    async (callbacks?: MutationCallbacks<{ menuItemCorrelationId: string }>) => {
      const addMenuItemPayload: any = {
        ...values,
        categoryCorrelationId: menuCategoryId,
      };
      delete addMenuItemPayload.isActive;

      // Handle empty printer - don't include it if empty
      if (addMenuItemPayload.storePrinterCorrelationId === "" || addMenuItemPayload.storePrinterCorrelationId === null) {
        delete addMenuItemPayload.storePrinterCorrelationId;
      }

      // Handle empty tags - set to null if empty array
      if (!addMenuItemPayload.itemTagCorrelationIdList || addMenuItemPayload.itemTagCorrelationIdList.length === 0) {
        addMenuItemPayload.itemTagCorrelationIdList = null;
      }

      const legacy = syncLegacyFieldsFromLocaleTranslations(values.localeTranslations);
      addMenuItemPayload.englishName = legacy.englishName;
      addMenuItemPayload.nonEnglishName = legacy.nonEnglishName;
      addMenuItemPayload.englishDescription = legacy.englishDescription;
      addMenuItemPayload.nonEnglishDescription = legacy.nonEnglishDescription;
      addMenuItemPayload.translations = buildTranslationsPayload(values.localeTranslations, enabledLocales);
      delete addMenuItemPayload.localeTranslations;

      addMenuItemMutation(addMenuItemPayload, callbacks);
    },
    [addMenuItemMutation, menuCategoryId, values, enabledLocales]
  );

  return { formik, addMenuItem, isAddingMenuItem };
};
